import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, File, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FileUpload = ({ onFilesUploaded, existingFiles = [] }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState(existingFiles);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const token = localStorage.getItem('token');
    const newFileIds = [];

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${BACKEND_URL}/api/files/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          const fileInfo = await response.json();
          newFileIds.push(fileInfo.id);
          setUploadedFiles(prev => [...prev, fileInfo]);
        } else {
          toast.error(`Erreur upload: ${file.name}`);
        }
      }

      if (newFileIds.length > 0) {
        onFilesUploaded([...uploadedFiles.map(f => f.id), ...newFileIds]);
        toast.success(`${newFileIds.length} fichier(s) uploadé(s)`);
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveFile = (fileId) => {
    const updated = uploadedFiles.filter(f => f.id !== fileId);
    setUploadedFiles(updated);
    onFilesUploaded(updated.map(f => f.id));
  };

  const getFileIcon = (filename) => {
    if (!filename) return '📎';
    const ext = filename.split('.').pop()?.toLowerCase();
    if (!ext) return '📎';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️';
    if (['pdf'].includes(ext)) return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx'].includes(ext)) return '📊';
    return '📎';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="file"
          id="file-upload"
          multiple
          onChange={handleFileUpload}
          className="hidden"
          disabled={uploading}
          data-testid="file-input"
        />
        <label htmlFor="file-upload">
          <Button
            type="button"
            variant="outline"
            disabled={uploading}
            asChild
            data-testid="upload-btn"
          >
            <span className="cursor-pointer">
              {uploading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              {uploading ? 'Upload en cours...' : 'Ajouter des fichiers'}
            </span>
          </Button>
        </label>
      </div>

      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700 dark:text-slate-200">
            Fichiers joints ({uploadedFiles.length})
          </p>
          <div className="space-y-2">
            {uploadedFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-800 rounded border"
                data-testid={`uploaded-file-${file.id}`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <File className="h-4 w-4 text-gray-500 dark:text-slate-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700 dark:text-slate-200 truncate">
                    {getFileIcon(file.filename)} {file.filename}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-slate-400">
                    ({(file.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveFile(file.id)}
                  data-testid={`remove-file-${file.id}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
